import React, { useEffect, useRef, useState } from 'react';
import './portalForm.style.scss';
import {
    FormDetail,
    FormShowType,
    ReduxState,
    Answer,
    Question,
    AnswerItem,
    AnswerStatus,
    ViewApi,
    QuestionTypes,
    QuestionValidationTypes,
    PrevFormDetail,
    Response,
} from 'interface';
import { connect, ConnectedProps } from 'react-redux';
import { post } from 'scripts/api';
import { Spinner } from 'react-bootstrap';
import SLPStartPage from '../../SLP/startPage/startPage.index';
import SLPEndPage from '../../SLP/endPage/endPage.index';
import SLPLayout, { SLPLayoutRef } from '../../SLP/SLPLayout/SLPLayout.index';
import { offlineKioskSubmit } from '../../kiosk/kiosk.scripts';
import { kioskCode, portalAnswers, portalData } from 'scripts/storage';
import SLPFooter from '../../SLP/footer/footer.index';
import SLPWizard from '../../SLP/wizard/wizard.index';
import { API } from 'data';
import useInterval from 'scripts/useInterval';
import { emailValidation, urlValidation } from 'scripts/validation';
import { toast } from 'react-toastify';
import { __PortalFormProps } from './portalForm.interface';
import { setPortalPublishData } from 'redux/actions';
import { detailForm2PreForm, setInitialAnswerList, SLPLogicalRelationship } from 'ucheck/SLP/SLP.script';
import { answerErrToast, viewErrMessage } from 'ucheck/SLP/SLP.data';
import { __AnswerApiErrType, __ViewApiErrType } from 'ucheck/SLP/SLP.interface';
import useWinEventListener from 'scripts/useWinEventListener';
import useOnIdle from 'scripts/useIdle';

const PortalForm: React.FC<ConnectedProps<typeof connector> & __PortalFormProps> = function (
    props: ConnectedProps<typeof connector> & __PortalFormProps,
) {
    const [step, setStep] = useState<'start' | 'end' | number>();
    const [formDetail, setFormDetail] = useState<FormDetail>();
    const [wizardIndex, setWizardIndex] = useState<number>(0);
    const qRefs = useRef<SLPLayoutRef[]>([]);
    const [answer, setAnswer] = useState<Answer>();
    const questionBox = useRef<HTMLDivElement[]>([]);
    const [answerList, setAnswerList] = useState<AnswerItem[]>([]);
    const view = useRef('');
    const [initialForm, setInitialForm] = useState<PrevFormDetail>();
    const lang = props.lang.kiosk;
    const [errMsg, setErrMsg] = useState<string[]>();
    const answerToken = useRef<string>();
    const [questions, setQuestion] = useState<Question[][]>([[]]);
    const numericalStep = step !== 'start' && step !== 'end' && step !== undefined ? step : undefined;
    const autoNextFlag = useRef<boolean>(false);
    const startClicked = useRef<boolean>(false);
    let currentQuestion: Question | undefined = undefined;
    if (
        formDetail?.show_type === FormShowType.single &&
        numericalStep !== undefined &&
        questions[numericalStep] &&
        questions[numericalStep][wizardIndex]
    ) {
        currentQuestion = questions[numericalStep][wizardIndex];
    } else if (
        numericalStep !== undefined &&
        questions[numericalStep] &&
        questions[numericalStep].length &&
        questions[numericalStep][questions[numericalStep].length - 1]
    ) {
        currentQuestion = questions[numericalStep][questions[numericalStep].length - 1];
    }

    useInterval(() => {
        offlineKioskSubmit(answer?.id, formDetail?.workspace.id);
    }, 60 * 1000);

    useInterval(() => {
        if (kioskCode && kioskCode.get())
            post(API.publish.updateOnline(kioskCode.get()!), {}).then((res) => {
                // console.log(res);
                if (res.status === 404) logout();
            });
    }, 2 * 60 * 1000);

    useEffect(() => {
        onInit();
    }, []);

    useEffect(() => {
        if (formDetail) {
            if (!formDetail.hide_start_page && step === 'start') onNext();
            else if (formDetail.hide_start_page && step === undefined) setStep(0);
        }
    }, [formDetail]);

    useEffect(() => {
        if (step === 'start' && questions.length && questions[0].length && startClicked.current) {
            startClicked.current = false;
            onNext();
        }
    }, [questions]);

    useEffect(() => {
        if (
            initialForm !== undefined &&
            initialForm.hide_start_page &&
            formDetail === undefined &&
            step === undefined
        ) {
            onStart();
        }
    }, [initialForm]);

    useEffect(() => {
        if (formDetail) {
            const checkKeys = (object: any) => {
                if (typeof object === 'object' && object !== null) {
                    for (let i = 0; i < Object.keys(object).length; i++) {
                        checkKeys(object[Object.keys(object)[i]]);
                    }
                } else if (Array.isArray(object)) {
                    for (let i = 0; i < object.length; i++) {
                        checkKeys(object[i]);
                    }
                } else if (urlValidation(object) && !emailValidation(object) && typeof object === 'string') {
                    const image = new Image();
                    image.src = object;
                }
            };
            checkKeys(formDetail);
        }
    }, [formDetail]);

    useWinEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            onNext();
        }
    });

    function onInit() {
        setWizardIndex(0);
        setStep(undefined);
        setAnswer(undefined);
        setAnswerList([]);
        setFormDetail(undefined);
        setInitialForm(undefined);
        return new Promise((resolve) => {
            post<ViewApi>(API.answers.addView, { publish: props.publish })
                .then((res) => {
                    if (res.data.view_id) {
                        view.current = res.data.view_id.toString();
                        if (!res.data.form.hide_start_page) setStep('start');
                        setInitialForm(res.data.form);
                    }
                })
                .catch((res: Response<__ViewApiErrType>) => {
                    if (res.status >= 500) toast.error(props.lang.serverErr);
                    else if (res.status >= 400) setErrMsg(viewErrMessage(res.data.non_field_errors.map((i) => i.code)));
                    else {
                        // offline mode
                        resolve(true);
                        view.current = '0';
                        const form = props.portalPublish?.portal.publish_set.find(
                            (i) => i.uuid === props.publish,
                        )?.form;
                        if (form) setInitialForm(detailForm2PreForm(form));
                        if (!form?.hide_start_page) setStep('start');
                    }
                });
        });
    }

    function onStart(pass?: string) {
        // if (/* !props.kiosk || */ !initialForm) return;
        if (!initialForm || view.current.length === 0) return;
        return new Promise((resolve) => {
            startClicked.current = true;
            const params: any = {
                view: view.current,
            };
            if (pass) params.password = pass;
            post<Answer>(API.answers.addAnswer, params)
                .then((res) => {
                    answerToken.current = res.data.access_token;
                    setFormDetail(res.data.form);
                    setAnswer({ ...res.data, items: [] });
                    setAnswerList(setInitialAnswerList([], currentQuestion?.id, res.data.form));
                })
                .catch((res: Response<__AnswerApiErrType>) => {
                    if (res.status >= 500) toast.error(props.lang.serverErr);
                    else if (view.current.length && view.current !== '0' && res.status >= 400)
                        answerErrToast(res.data.non_field_errors.map((i) => i.code));
                    else {
                        if (pass) toast.error(lang.noInternet);
                        else {
                            //  handleOffline no password
                            const id = new Date().getTime();
                            const createdAt = new Date().toISOString();
                            const form = props.portalPublish?.portal.publish_set.find(
                                (i) => i.uuid === props.publish,
                            )?.form;
                            if (!form) return;
                            setFormDetail(form);
                            setAnswerList(setInitialAnswerList([], currentQuestion?.id, form));
                            portalAnswers.addAnswer(id, createdAt, form.id, props.publish);
                            setAnswer({
                                items: [],
                                created_at: createdAt,
                                modified_at: createdAt,
                                state: AnswerStatus.incomplete,
                                form: form,
                                id,
                                publish: 0,
                            });
                            onNext();
                        }
                    }
                })
                .finally(() => resolve(true));
        });
    }

    function onNext() {
        if (!formDetail || step === undefined) return;
        if (step === 'start' && questions.length && questions[0].length) {
            // start and go to questions
            setStep(0);
            setWizardIndex(0);
        } else if (step !== 'start' && step !== 'end' && formDetail.show_type === FormShowType.single) {
            if (qRefs.current[wizardIndex]?.validate(true)) {
                if (formDetail?.show_type === FormShowType.single) qRefs.current[wizardIndex]?.submit(false);
                if (wizardIndex < questions[step].length - 1) setWizardIndex(wizardIndex + 1);
                else {
                    setStep('end');
                    if (answerToken.current) post(API.answers.closeAnswer(answerToken.current), {});
                }
            }
        } else if (step !== 'start' && step !== 'end' && formDetail.show_type === FormShowType.multiple) {
            let errorIndex: number | undefined = undefined;
            qRefs.current.forEach((question, index) => {
                if (!question.validate(true) && errorIndex === undefined) errorIndex = index;
            });
            if (errorIndex !== undefined) {
                questionBox.current[errorIndex].scrollIntoView();
            } else {
                qRefs.current.forEach((question, index) => {
                    question.submit(true);
                });
                if (step < questions.length - 1) {
                    setStep(step + 1);
                    setWizardIndex(0);
                } else {
                    setStep('end');
                    if (answerToken.current) post(API.answers.closeAnswer(answerToken.current), {});
                }
            }
        }
    }

    function onPrev(page?: boolean) {
        if (!formDetail || step === undefined) return;
        if (page && step !== 'start' && step !== 'end' && step > 0) {
            // go to previous page
            setStep(step - 1);
            setWizardIndex(0);
        } else if (
            step !== 'start' &&
            step !== 'end' &&
            wizardIndex > 0 &&
            formDetail.show_type === FormShowType.single
        ) {
            // go to previous question
            setWizardIndex(wizardIndex - 1);
        } else if (step !== 'start' && step !== 'end' && wizardIndex === 0 && step > 0) {
            // go to previous page
            setStep(step - 1);
            setWizardIndex(0);
        }
    }

    useEffect(() => {
        let ques: Question[][] = [];
        if (formDetail?.show_type === FormShowType.single) {
            const temp = SLPLogicalRelationship(formDetail, answerList, currentQuestion?.id, answer?.answerer_info);
            ques = [[]];
            for (const list of temp) {
                ques = [[...ques[0], ...list.sort((a, b) => a.order - b.order)]];
            }
        } else if (formDetail) {
            const temp = SLPLogicalRelationship(formDetail, answerList, currentQuestion?.id, answer?.answerer_info);
            ques = [];
            for (const list of temp) {
                if (list.length) ques.push([...list.sort((a, b) => a.order - b.order)]);
            }
        }
        setQuestion(ques);
    }, [formDetail, answerList, answer, wizardIndex, step]);

    useOnIdle(() => {
        if (formDetail && answer) window.location.reload();
    }, formDetail?.reset_sec_time);

    function onSlideChange(e: number) {
        if (formDetail?.show_type === FormShowType.multiple) qRefs.current[wizardIndex]?.submit(false);
        setWizardIndex(e);
    }

    function logout() {
        portalData.remove();
        kioskCode.remove();
        props.dispatch(setPortalPublishData(null));
    }

    function submitAnswerItem(item: AnswerItem) {
        function offlineHandler() {
            if (!answer) return;
            portalAnswers.setAnswerItem(answer.id, { ...item, questionerOffline: true });
        }
        if (answer && portalAnswers.checkAnswerCreationStatus(answer.id)) offlineHandler();
        else {
            const objApi: any = {
                value: item.value,
                answer: answer?.id,
                question: item.question.id,
                answer_files: item.answer_files?.map((i) => i.id),
            };
            if (!item.value) objApi.question_items = item.question_items?.map((i) => i.id);
            if (answerToken.current) post<AnswerItem>(API.answers.answerItem(answerToken.current), objApi);
        }
    }

    // auto next for some question types
    useEffect(() => {
        if (
            autoNextFlag.current &&
            formDetail?.show_type === FormShowType.single &&
            answerList.length > 0 &&
            step !== undefined &&
            step !== 'end' &&
            step !== 'start' &&
            wizardIndex < questions[step].length - 1
        ) {
            const question = questions[step][wizardIndex];
            const answerItem = answerList.find((item) => item.question.id === question.id);
            if (answerItem && wizardIndex < questions[step].length - 1) {
                if (
                    question.input_type === QuestionTypes.dropDown ||
                    question.input_type === QuestionTypes.twoOptions ||
                    question.input_type === QuestionTypes.nps ||
                    question.input_type === QuestionTypes.emoji ||
                    question.input_type === QuestionTypes.rate ||
                    (question.input_type === QuestionTypes.selective &&
                        !question.validations?.find((i) => i.title === QuestionValidationTypes.selective)?.value &&
                        answerItem.question_items?.length &&
                        answerItem.question_items[0].value !== '1') ||
                    (question.input_type === QuestionTypes.pictureSelective &&
                        !question.validations?.find((i) => i.title === QuestionValidationTypes.selective)?.value &&
                        answerItem.question_items?.length &&
                        answerItem.question_items[0].value !== '1')
                ) {
                    onNext();
                }
            }
        }
        if (autoNextFlag.current) autoNextFlag.current = false;
    }, [questions]);

    return (
        <div className={`ucheck-portal-form-slp-higher-order`}>
            {errMsg ? (
                <div className="slp-no-view-box">
                    <div>
                        {errMsg.map((i) => (
                            <h4 key={i}>{i}</h4>
                        ))}
                    </div>
                </div>
            ) : (
                <div
                    style={{
                        background: initialForm?.background_image_file
                            ? 'url(' + initialForm?.background_image_file?.file + ')'
                            : initialForm?.background_color
                            ? initialForm.background_color
                            : undefined
                    }}
                    className="slp-view-box"
                >
                    {initialForm && step === 'start' ? (
                        <SLPStartPage onSecret={logout} next={(e) => onStart(e)} form={initialForm} />
                    ) : null}
                    {formDetail
                        ? questions.map((section, sectionIndex) => {
                              if (step !== sectionIndex) return null;
                              let reInit = false;
                              if (qRefs.current.length !== section.length) {
                                  qRefs.current = [];
                                  reInit = true;
                              }
                              questionBox.current = [];
                              return (
                                  <SLPWizard
                                      className="slp-body"
                                      key={sectionIndex}
                                      questions={section}
                                      form={formDetail}
                                      wizardIndex={wizardIndex}
                                      onChange={onSlideChange}
                                      sectionIndex={sectionIndex}
                                      layout={(quiz, index) => (
                                          <div
                                              ref={(ref) => {
                                                  if (ref) questionBox.current.push(ref);
                                              }}
                                              key={quiz.id}
                                          >
                                              <SLPLayout
                                                  ref={(ref) => {
                                                      if (ref && reInit) qRefs.current.push(ref);
                                                      else if (ref) qRefs.current[index] = ref;
                                                  }}
                                                  key={index}
                                                  form={formDetail}
                                                  questions={questions}
                                                  sectionIndex={sectionIndex}
                                                  questionIndex={index}
                                                  next={onNext}
                                                  previous={onPrev}
                                                  answerList={answerList}
                                                  setAnswerList={(answersList) => {
                                                      autoNextFlag.current = true;
                                                      setAnswerList(
                                                          setInitialAnswerList(
                                                              answersList,
                                                              currentQuestion?.id,
                                                              formDetail,
                                                          ),
                                                      );
                                                  }}
                                                  answer={answer}
                                                  submit={submitAnswerItem}
                                                  cancel={props.onFinish}
                                              />
                                          </div>
                                      )}
                                  />
                              );
                          })
                        : null}
                    {formDetail && step === 'end' ? <SLPEndPage startAgain={props.onFinish} form={formDetail} /> : null}
                    {(view.current.length === 0 && step !== 'end') ||
                    !initialForm ||
                    (!answer && initialForm.hide_start_page) ? (
                        <div className="loading-content">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : null}
                </div>
            )}
            {step !== undefined ? (
                <SLPFooter
                    form={formDetail}
                    question={step !== 'start' && step !== 'end' ? questions[step][wizardIndex] : undefined}
                    onNext={onNext}
                    onPrev={onPrev}
                    cover={false}
                    hide={initialForm?.hide_slp_footer}
                    onLogoClick={props.onFinish}
                />
            ) : undefined}
        </div>
    );
};

const mapStateToProps = (state: ReduxState) => ({
    direction: state.direction,
    lang: state.lang,
    portalPublish: state.portalPublishData,
});

const connector = connect(mapStateToProps);
export default connector(PortalForm);
